import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useWorkerPhoto, setWorkerPhotoCache } from '@/hooks/useWorkerPhoto';
import WorkerDocuments from '@/components/WorkerDocuments';
import { Button } from '@/components/ui/button';
import { 
  User,
  FileText,
  Wallet,
  History,
  Check,
  X,
  AlertTriangle,
  Plane,
  CheckCircle,
  CreditCard,
  Minus,
  Trash2,
  Key,
  Ticket,
  Calendar,
  Globe,
  Briefcase,
  Building2,
  ChevronLeft,
  Plus,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Camera,
  Upload,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPut } from '@/utils/api';

// Background images for each tab
const tabBackgrounds = {
  profile: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
  documents: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=1200&q=80',
  financial: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
  history: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
  warranty: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80'
};

// Tab configuration
const tabs = [
  { id: 'profile', label: 'Profile', labelAr: 'الملف', icon: User },
  { id: 'documents', label: 'Documents', labelAr: 'المستندات', icon: FileText },
  { id: 'financial', label: 'Financial', labelAr: 'المالية', icon: Wallet },
  { id: 'warranty', label: 'Warranty', labelAr: 'الضمان', icon: Shield },
  { id: 'history', label: 'History', labelAr: 'السجل', icon: History }
];

// Status configuration
const statusConfig = {
  'arrived': { bg: 'from-emerald-500 to-emerald-600', text: 'ARRIVED', textAr: 'وصل', icon: Check, color: 'emerald' },
  'abroad': { bg: 'from-blue-500 to-blue-600', text: 'ABROAD', textAr: 'بالخارج', icon: Plane, color: 'blue' },
  'runaway': { bg: 'from-red-500 to-red-600', text: 'RUNAWAY', textAr: 'هروب', icon: X, color: 'red' },
  'noarrival': { bg: 'from-amber-500 to-amber-600', text: 'NO ARRIVAL', textAr: 'لم يصل', icon: AlertTriangle, color: 'amber' },
  'settled': { bg: 'from-purple-500 to-purple-600', text: 'SETTLED', textAr: 'تسوية', icon: CheckCircle, color: 'purple' }
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

// Worker Header Component - Shows in ALL tabs
const WorkerHeader = ({ worker, onPhotoClick, uploadingPhoto }) => {
  const config = statusConfig[worker.status] || statusConfig['abroad'];
  const StatusIcon = config.icon;
  const initials = (worker.workerName || 'NA').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const photoSrc = useWorkerPhoto(worker);
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-4 mb-4 border border-white/10"
    >
      <div className="flex items-center gap-4">
        {/* Worker Photo with Upload Option */}
        <div className="relative group">
          {photoSrc ? (
            <div className="w-16 h-16 rounded-2xl border-2 border-orange-500/50 overflow-hidden shadow-lg shadow-orange-500/20 flex-shrink-0">
              <img src={photoSrc} alt={worker.workerName} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-2xl border-2 border-orange-500/50 bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl font-bold">{initials}</span>
            </div>
          )}
          
          {/* Upload Button Overlay */}
          <button
            onClick={onPhotoClick}
            disabled={uploadingPhoto}
            className="absolute inset-0 bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
          >
            {uploadingPhoto ? (
              <Loader2 className="h-6 w-6 text-white animate-spin" />
            ) : (
              <Camera className="h-6 w-6 text-white" />
            )}
          </button>
        </div>
        
        {/* Worker Info */}
        <div className="flex-1 min-w-0">
          <h2 className="text-white text-lg font-bold truncate">{(worker.workerName || 'N/A').toUpperCase()}</h2>
          <p className="text-orange-400 text-sm font-medium flex items-center gap-1">
            <Globe className="h-3 w-3" />
            {(worker.nationality || 'Unknown').toUpperCase()}
          </p>
          {/* Photo upload hint */}
          {!photoSrc && (
            <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
              <Upload className="h-3 w-3" />
              اضغط على الصورة للرفع
            </p>
          )}
        </div>
        
        {/* Status Badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r ${config.bg} flex-shrink-0`}>
          <StatusIcon className="h-4 w-4 text-white" />
          <span className="text-white text-xs font-bold">{config.text}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Profile Tab Content
const ProfileTab = ({ worker, onStatusChange, onWarrantyActivate, onWarrantyPeriodChange, isLocked }) => {
  const handleStatusClick = (newStatus) => {
    if (onStatusChange) {
      onStatusChange(worker.id, newStatus);
    }
  };
  
  const handleWarrantyActivate = () => {
    if (onWarrantyActivate) {
      onWarrantyActivate(worker.id);
    }
  };
  
  const handleWarrantyPeriodChange = (period) => {
    if (onWarrantyPeriodChange) {
      onWarrantyPeriodChange(worker.id, period);
    }
  };
  
  // Calculate warranty status color
  const getWarrantyStatusColor = () => {
    const status = worker.warrantyStatus || 'not_activated';
    if (status === 'critical') return { bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400', label: 'حرج / CRITICAL' };
    if (status === 'warning') return { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400', label: 'تحذير / WARNING' };
    if (status === 'active') return { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400', label: 'نشط / ACTIVE' };
    if (status === 'completed') return { bg: 'bg-slate-500/20', border: 'border-slate-500/40', text: 'text-slate-400', label: 'منتهي / COMPLETED' };
    return { bg: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-400', label: 'غير مفعل / NOT ACTIVATED' };
  };
  
  const warrantyColors = getWarrantyStatusColor();
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Personal Details */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <User className="h-5 w-5 text-cyan-400" />
          Personal Details / البيانات الشخصية
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Passport / الجواز</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <Key className="h-4 w-4 text-blue-400" />
              {worker.passportNumber || 'N/A'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Date of Birth / تاريخ الميلاد</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-400" />
              {worker.dateOfBirth || 'N/A'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Profession / المهنة</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-emerald-400" />
              {worker.profession || 'N/A'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Sponsor / الكفيل</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-amber-400" />
              {worker.sponsorName || 'N/A'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Warranty Section */}
      <div className={`bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border ${warrantyColors.border}`}>
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-violet-400" />
          Warranty / الضمان
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold ${warrantyColors.bg} ${warrantyColors.text}`}>
            {warrantyColors.label}
          </span>
        </h3>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className={`rounded-xl p-3 border ${warrantyColors.border} ${warrantyColors.bg}`}>
            <p className="text-slate-400 text-xs mb-1">Warranty Period / مدة الضمان</p>
            <p className={`font-bold text-lg ${warrantyColors.text}`}>
              {worker.warrantyPeriod || 3} شهور / Months
            </p>
          </div>
          
          {worker.warrantyActivated ? (
            <>
              <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
                <p className="text-slate-400 text-xs mb-1">Activation Date / تاريخ التفعيل</p>
                <p className="text-white font-medium text-sm">
                  {worker.warrantyActivationDate || 'N/A'}
                </p>
              </div>
              
              <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
                <p className="text-slate-400 text-xs mb-1">Expiry Date / تاريخ الانتهاء</p>
                <p className="text-white font-medium text-sm">
                  {worker.warrantyExpiryDate || 'N/A'}
                </p>
              </div>
              
              <div className={`rounded-xl p-3 border ${warrantyColors.border} ${warrantyColors.bg}`}>
                <p className="text-slate-400 text-xs mb-1">Days Remaining / الأيام المتبقية</p>
                <p className={`font-bold text-2xl ${warrantyColors.text}`}>
                  {worker.warrantyDaysRemaining || 0}
                </p>
              </div>
            </>
          ) : (
            <div className="bg-violet-500/10 rounded-xl p-3 border border-violet-500/30 flex items-center justify-center">
              <p className="text-violet-400 text-sm text-center">
                الضمان غير مفعل بعد<br/>
                <span className="text-xs text-slate-500">Warranty not activated</span>
              </p>
            </div>
          )}
        </div>
        
        {/* Warranty Actions */}
        {!isLocked && (
          <div className="flex flex-wrap gap-2">
            {/* Period Selection — visible BEFORE and AFTER activation */}
            <div className="flex items-center gap-2 bg-slate-800/60 rounded-xl px-3 py-2">
              <span className="text-slate-400 text-xs">Period:</span>
              <button
                onClick={() => {
                  if (worker.warrantyActivated && (worker.warrantyPeriod || 3) !== 3) {
                    if (!window.confirm(
                      'تغيير المدة سيُعيد حساب تاريخ انتهاء الضمان.\nChange period to 3 months? This will recalculate the expiry date.'
                    )) return;
                  }
                  handleWarrantyPeriodChange(3);
                }}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  (worker.warrantyPeriod || 3) === 3
                    ? 'bg-violet-500 text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                }`}
                data-testid="warranty-period-3-btn"
              >
                3 شهور
              </button>
              <button
                onClick={() => {
                  if (worker.warrantyActivated && (worker.warrantyPeriod || 3) !== 6) {
                    if (!window.confirm(
                      'تغيير المدة سيُعيد حساب تاريخ انتهاء الضمان.\nChange period to 6 months? This will recalculate the expiry date.'
                    )) return;
                  }
                  handleWarrantyPeriodChange(6);
                }}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  (worker.warrantyPeriod || 3) === 6
                    ? 'bg-violet-500 text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                }`}
                data-testid="warranty-period-6-btn"
              >
                6 شهور
              </button>
            </div>

            {/* Activation block — only when NOT activated */}
            {!worker.warrantyActivated && (
              <>
                {(worker.status === 'arrived' || worker.arrivalStatus === 'inside_country') && (
                  <Button
                    onClick={handleWarrantyActivate}
                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold rounded-xl flex-1"
                    data-testid="activate-warranty-btn"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    تفعيل الضمان / Activate Warranty
                  </Button>
                )}

                {worker.status !== 'arrived' && worker.arrivalStatus !== 'inside_country' && (
                  <div className="flex-1 bg-slate-800/60 rounded-xl px-4 py-2 text-center">
                    <p className="text-slate-500 text-xs">
                      يجب وصول العامل أولاً لتفعيل الضمان
                      <br/>
                      <span className="text-[10px]">Worker must arrive first to activate warranty</span>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Status Actions */}
      {!isLocked && (
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
          <h3 className="text-white text-base font-semibold mb-4">Change Status / تغيير الحالة</h3>
          <div className="grid grid-cols-3 gap-3">
            {worker.status === 'abroad' && (
              <Button
                onClick={() => handleStatusClick('arrived')}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold rounded-xl py-5"
                data-testid="status-arrived-btn"
              >
                <Check className="h-5 w-5 mr-2" />
                <span className="text-sm">وصل<br/>ARRIVED</span>
              </Button>
            )}
            <Button
              onClick={() => handleStatusClick('runaway')}
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl py-5"
              data-testid="status-runaway-btn"
            >
              <X className="h-5 w-5 mr-2" />
              <span className="text-sm">هروب<br/>RUNAWAY</span>
            </Button>
            <Button
              onClick={() => handleStatusClick('noarrival')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold rounded-xl py-5"
              data-testid="status-noarrival-btn"
            >
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span className="text-sm">لم يصل<br/>NO ARRIVAL</span>
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Warranty Tab Content - Full Page with Large Ring
const WarrantyTab = ({ worker, onWarrantyActivate, onWarrantyPeriodChange, isLocked }) => {
  // Calculate warranty details
  const totalDays = (worker.warrantyPeriod || 3) * 30;
  const daysRemaining = worker.warrantyDaysRemaining || 0;
  const elapsed = totalDays - daysRemaining;
  const progressPercent = worker.warrantyActivated ? Math.min(100, Math.max(0, (elapsed / totalDays) * 100)) : 0;
  
  const getWarrantyColor = () => {
    if (!worker.warrantyActivated) return { ring: '#8B5CF6', text: 'text-violet-400', bg: 'bg-violet-500/20', border: 'border-violet-500/30' };
    if (worker.warrantyStatus === 'critical') return { ring: '#EF4444', text: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' };
    if (worker.warrantyStatus === 'warning') return { ring: '#F59E0B', text: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' };
    if (worker.warrantyStatus === 'completed') return { ring: '#10B981', text: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' };
    return { ring: '#10B981', text: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' };
  };
  
  const colors = getWarrantyColor();
  
  const getStatusLabel = () => {
    if (!worker.warrantyActivated) return { en: 'NOT ACTIVATED', ar: 'غير مفعل' };
    if (worker.warrantyStatus === 'critical') return { en: 'CRITICAL', ar: 'حرج' };
    if (worker.warrantyStatus === 'warning') return { en: 'WARNING', ar: 'تحذير' };
    if (worker.warrantyStatus === 'completed') return { en: 'COMPLETED', ar: 'منتهي' };
    return { en: 'ACTIVE', ar: 'نشط' };
  };
  
  const statusLabel = getStatusLabel();
  
  const handleWarrantyActivate = () => {
    if (onWarrantyActivate) {
      onWarrantyActivate(worker.id);
    }
  };
  
  const handleWarrantyPeriodChange = (period) => {
    if (onWarrantyPeriodChange) {
      onWarrantyPeriodChange(worker.id, period);
    }
  };
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-6"
    >
      {/* Large Warranty Ring Section */}
      <div className={`bg-slate-900/80 backdrop-blur-xl rounded-3xl p-8 border ${colors.border}`}>
        {/* Header with Status */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
              <Shield className={`h-6 w-6 ${colors.text}`} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">Warranty / الضمان</h2>
              <p className="text-slate-500 text-sm">Worker warranty tracking</p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
            {statusLabel.ar} / {statusLabel.en}
          </span>
        </div>
        
        {/* Large Ring Display */}
        <div className="flex flex-col items-center justify-center py-6">
          <div className="relative w-48 h-48">
            {/* Background circle */}
            <svg className="w-48 h-48 -rotate-90" viewBox="0 0 200 200">
              <circle 
                cx="100" cy="100" r="85" 
                fill="none" 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="12" 
              />
              {/* Progress circle */}
              <circle 
                cx="100" cy="100" r="85" 
                fill="none" 
                stroke={colors.ring}
                strokeWidth="12" 
                strokeLinecap="round"
                strokeDasharray={`${progressPercent * 5.34} 534`}
                className="transition-all duration-1000"
              />
            </svg>
            
            {/* Center content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {worker.warrantyActivated ? (
                <>
                  <span className={`text-5xl font-black ${colors.text}`}>
                    {daysRemaining}
                  </span>
                  <span className="text-slate-500 text-sm uppercase tracking-wider">يوم / Days</span>
                </>
              ) : (
                <>
                  <ShieldAlert className={`h-12 w-12 ${colors.text} mb-2`} />
                  <span className="text-slate-500 text-xs text-center">غير مفعل<br/>Not Activated</span>
                </>
              )}
            </div>
          </div>
          
          {/* Progress percentage */}
          {worker.warrantyActivated && (
            <div className="mt-4 text-center">
              <span className={`text-lg font-bold ${colors.text}`}>{Math.round(progressPercent)}%</span>
              <span className="text-slate-500 text-sm ml-2">مستهلك / Used</span>
            </div>
          )}
        </div>
        
        {/* Warranty Details Grid */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className={`rounded-2xl p-4 ${colors.bg} border ${colors.border}`}>
            <p className="text-slate-400 text-xs mb-2">مدة الضمان / Warranty Period</p>
            <p className={`text-2xl font-black ${colors.text}`}>
              {worker.warrantyPeriod || 3} <span className="text-base font-normal">شهور / Months</span>
            </p>
          </div>
          
          {worker.warrantyActivated ? (
            <div className={`rounded-2xl p-4 ${colors.bg} border ${colors.border}`}>
              <p className="text-slate-400 text-xs mb-2">المتبقي / Remaining</p>
              <p className={`text-2xl font-black ${colors.text}`}>
                {daysRemaining} <span className="text-base font-normal">يوم / Days</span>
              </p>
            </div>
          ) : (
            <div className="rounded-2xl p-4 bg-slate-800/50 border border-slate-700/50">
              <p className="text-slate-400 text-xs mb-2">المتبقي / Remaining</p>
              <p className="text-slate-500 text-lg font-medium">— يوم</p>
            </div>
          )}
        </div>
        
        {/* Dates Section */}
        {worker.warrantyActivated && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-slate-800/60 rounded-xl p-4 border border-white/5">
              <p className="text-slate-400 text-xs mb-2 flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                تاريخ التفعيل / Activation Date
              </p>
              <p className="text-white font-semibold">{worker.warrantyActivationDate || 'N/A'}</p>
            </div>
            
            <div className="bg-slate-800/60 rounded-xl p-4 border border-white/5">
              <p className="text-slate-400 text-xs mb-2 flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                تاريخ الانتهاء / Expiry Date
              </p>
              <p className="text-white font-semibold">{worker.warrantyExpiryDate || 'N/A'}</p>
            </div>
          </div>
        )}
        
        {/* Progress Bar */}
        {worker.warrantyActivated && (
          <div className="mt-6">
            <div className="flex justify-between text-xs mb-2">
              <span className="text-slate-500">Progress / التقدم</span>
              <span className={`font-medium ${colors.text}`}>{statusLabel.ar}</span>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-1000`}
                style={{ 
                  width: `${progressPercent}%`,
                  background: `linear-gradient(90deg, ${colors.ring}, ${colors.ring}dd)`
                }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Warranty Actions */}
      {!isLocked && !worker.warrantyActivated && (
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-violet-500/30">
          <h3 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-violet-400" />
            تفعيل الضمان / Activate Warranty
          </h3>
          
          {/* Period Selection */}
          <div className="mb-6">
            <p className="text-slate-400 text-sm mb-3">اختر مدة الضمان / Select Warranty Period:</p>
            <div className="flex gap-4">
              <button
                onClick={() => handleWarrantyPeriodChange(3)}
                className={`flex-1 py-4 rounded-xl text-lg font-bold transition-all ${
                  (worker.warrantyPeriod || 3) === 3 
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30' 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid="warranty-period-3-months"
              >
                3 شهور / Months
              </button>
              <button
                onClick={() => handleWarrantyPeriodChange(6)}
                className={`flex-1 py-4 rounded-xl text-lg font-bold transition-all ${
                  (worker.warrantyPeriod || 3) === 6 
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30' 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid="warranty-period-6-months"
              >
                6 شهور / Months
              </button>
            </div>
          </div>
          
          {/* Activate Button */}
          {(worker.status === 'arrived' || worker.arrivalStatus === 'inside_country') ? (
            <Button
              onClick={handleWarrantyActivate}
              className="w-full py-6 bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 hover:from-violet-600 hover:via-purple-600 hover:to-pink-600 text-white text-lg font-bold rounded-xl shadow-xl shadow-violet-500/30"
              data-testid="activate-warranty-full-btn"
            >
              <ShieldCheck className="h-6 w-6 mr-3" />
              تفعيل الضمان الآن / ACTIVATE WARRANTY NOW
            </Button>
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
              <p className="text-amber-400 font-medium">يجب وصول العامل أولاً لتفعيل الضمان</p>
              <p className="text-slate-500 text-sm mt-1">Worker must arrive first to activate warranty</p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Documents Tab Content
const DocumentsTab = ({ worker }) => {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Attached Documents (images + PDF) */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-orange-400" />
          المستندات المرفقة / Attached Documents
        </h3>
        <p className="text-slate-500 text-xs mb-3">
          تظهر هذه المستندات في قسم الفيزا، وتُحذف تلقائياً عند حذف الفيزا.
        </p>
        <WorkerDocuments caseId={worker.id} />
      </div>

      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-emerald-400" />
          Visa / التأشيرة
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-xl p-3 border ${worker.visaNumber ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <p className="text-slate-400 text-xs mb-1">Visa Number / رقم التأشيرة</p>
            <p className={`font-medium text-sm ${worker.visaNumber ? 'text-emerald-400' : 'text-red-400'}`}>
              {worker.visaNumber || 'Not Available'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Visa Expiry / انتهاء التأشيرة</p>
            <p className="text-white font-medium text-sm">{worker.visaExpiryDate || 'N/A'}</p>
          </div>
        </div>
      </div>
      
      {/* Passport Information */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <Key className="h-5 w-5 text-blue-400" />
          Passport / الجواز
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-xl p-3 border ${worker.passportNumber ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-500/10 border-slate-500/30'}`}>
            <p className="text-slate-400 text-xs mb-1">Passport Number / رقم الجواز</p>
            <p className={`font-medium text-sm ${worker.passportNumber ? 'text-blue-400' : 'text-slate-500'}`}>
              {worker.passportNumber || 'Not Available'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Passport Expiry / انتهاء الجواز</p>
            <p className="text-white font-medium text-sm">{worker.passportExpiry || 'N/A'}</p>
          </div>
        </div>
        
        {/* Passport Sent Status */}
        <div className={`mt-3 rounded-xl p-3 border ${worker.passportSent ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs mb-1">Delivery Status / حالة التسليم</p>
              <p className={`font-medium text-sm ${worker.passportSent ? 'text-emerald-400' : 'text-amber-400'}`}>
                {worker.passportSent ? '✓ Sent / تم التسليم' : '⏳ Not Sent / لم يُسلم'}
              </p>
            </div>
            {worker.passportSentDate && (
              <p className="text-slate-400 text-xs">{worker.passportSentDate}</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Flight Ticket */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5 text-cyan-400" />
          Flight Ticket / تذكرة الطيران
        </h3>
        
        <div className={`rounded-xl p-4 border ${worker.linkedTicketId ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-slate-500/10 border-slate-500/30'}`}>
          <p className={`font-medium ${worker.linkedTicketId ? 'text-cyan-400' : 'text-slate-500'}`}>
            {worker.linkedTicketId ? '✓ Flight Ticket Linked / التذكرة مرتبطة' : 'No Ticket / لا توجد تذكرة'}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Financial Tab Content
const FinancialTab = ({ worker, currencySymbol, onPayment, onDeduction, onFullyPaid, onSettle, onAmendment, isLocked }) => {
  const hasAmendments = (worker.contractAmendments || []).length > 0;
  const canSettle = (worker.status === 'runaway' || worker.status === 'noarrival') && worker.status !== 'settled';
  const showFullyPaid = (worker.remainingAmount || 0) > 0;
  
  // Format number for display
  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Contract Summary */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-400" />
            Contract / العقد
          </h3>
          {!isLocked && (
            <Button
              onClick={() => onAmendment(worker)}
              variant="ghost"
              size="sm"
              className={`${hasAmendments ? 'text-amber-400 hover:text-amber-300' : 'text-slate-400 hover:text-white'}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              {hasAmendments ? `${(worker.contractAmendments || []).length} Amendments` : 'Add'}
            </Button>
          )}
        </div>
        
        {/* Amendments Breakdown */}
        {hasAmendments && (
          <div className="mb-4 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-400">Original / الأصلي:</span>
              <span className="text-white font-medium">{currencySymbol} {formatNumber(worker.originalTotalCost || worker.totalCost)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-amber-400">+ Amendments / ملحقات:</span>
              <span className="text-amber-400 font-medium">+{currencySymbol} {formatNumber(worker.totalAmendments)}</span>
            </div>
          </div>
        )}
        
        {/* Main Financial Stats */}
        <div className="space-y-3">
          {/* Total */}
          <div className="bg-slate-800/60 rounded-xl p-4 border border-white/5">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Total Contract / إجمالي العقد</span>
              <span className="text-white text-xl font-bold">{currencySymbol} {formatNumber(worker.totalCost)}</span>
            </div>
          </div>
          
          {/* Paid */}
          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30">
            <div className="flex justify-between items-center">
              <span className="text-emerald-300 text-sm">Paid / المدفوع</span>
              <span className="text-emerald-400 text-xl font-bold">{currencySymbol} {formatNumber(worker.paidAmount)}</span>
            </div>
          </div>
          
          {/* Deducted */}
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <div className="flex justify-between items-center">
              <span className="text-red-300 text-sm">Deducted / الخصومات</span>
              <span className="text-red-400 text-xl font-bold">{currencySymbol} {formatNumber(worker.deductionsUSD)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Remaining Amount */}
      <div className={`rounded-2xl p-5 border ${
        worker.status === 'settled' 
          ? 'bg-purple-500/20 border-purple-500/40' 
          : 'bg-orange-500/20 border-orange-500/40'
      }`}>
        <p className="text-center text-sm mb-2 text-slate-300">
          {worker.status === 'settled' ? 'SETTLED / تمت التسوية' : 'REMAINING / المتبقي'}
        </p>
        <p className={`text-center text-4xl font-black ${
          worker.status === 'settled' ? 'text-purple-400' : 'text-orange-400'
        }`}>
          {worker.status === 'settled' ? `${currencySymbol} 0` : `${currencySymbol} ${formatNumber(worker.remainingAmount)}`}
        </p>
      </div>
      
      {/* Action Buttons */}
      {!isLocked && (
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
          <h3 className="text-white text-base font-semibold mb-4">Actions / الإجراءات</h3>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => onPayment(worker)}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold rounded-xl py-5"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              <span className="text-sm">دفع<br/>PAYMENT</span>
            </Button>
            
            <Button
              onClick={() => onDeduction(worker)}
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl py-5"
            >
              <Minus className="h-5 w-5 mr-2" />
              <span className="text-sm">خصم<br/>DEDUCTION</span>
            </Button>
          </div>
          
          {showFullyPaid && (
            <Button
              onClick={() => onFullyPaid(worker)}
              className="w-full mt-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-slate-900 font-bold rounded-xl py-5"
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              تم الدفع بالكامل / MARK FULLY PAID
            </Button>
          )}
        </div>
      )}
      
      {/* Settle Button for Runaway/NoArrival */}
      {canSettle && (
        <Button
          onClick={() => onSettle(worker)}
          className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold rounded-xl py-5"
        >
          <CheckCircle className="h-5 w-5 mr-2" />
          تسوية الدين / SETTLE DEBT
        </Button>
      )}
    </motion.div>
  );
};

// History Tab Content
const HistoryTab = ({ worker, currencySymbol }) => {
  const paymentLog = worker.paymentLog || [];
  const deductionLog = worker.deductionLog || [];
  const amendments = worker.contractAmendments || [];
  
  // Combine all logs and sort by date
  const allLogs = [
    ...paymentLog.map(p => ({ ...p, type: 'payment' })),
    ...deductionLog.map(d => ({ ...d, type: 'deduction' })),
    ...amendments.map(a => ({ ...a, type: 'amendment' }))
  ].sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date));
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <History className="h-5 w-5 text-cyan-400" />
          Transaction History / سجل المعاملات
        </h3>
        
        {allLogs.length === 0 ? (
          <div className="text-center py-8">
            <History className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No transactions yet / لا توجد معاملات</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {allLogs.map((log, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl p-3 border ${
                  log.type === 'payment' 
                    ? 'bg-emerald-500/10 border-emerald-500/20' 
                    : log.type === 'deduction'
                    ? 'bg-red-500/10 border-red-500/20'
                    : 'bg-amber-500/10 border-amber-500/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      log.type === 'payment' ? 'bg-emerald-500/20' : 
                      log.type === 'deduction' ? 'bg-red-500/20' : 'bg-amber-500/20'
                    }`}>
                      {log.type === 'payment' ? <CreditCard className="h-4 w-4 text-emerald-400" /> : 
                       log.type === 'deduction' ? <Minus className="h-4 w-4 text-red-400" /> : 
                       <FileText className="h-4 w-4 text-amber-400" />}
                    </div>
                    <div>
                      <p className={`font-bold text-sm ${
                        log.type === 'payment' ? 'text-emerald-400' : 
                        log.type === 'deduction' ? 'text-red-400' : 'text-amber-400'
                      }`}>
                        {log.type === 'payment' ? '+' : log.type === 'deduction' ? '-' : '+'}
                        {currencySymbol} {(log.amount || 0).toLocaleString()}
                      </p>
                      <p className="text-slate-500 text-xs">
                        {log.type === 'payment' ? 'Payment' : log.type === 'deduction' ? 'Deduction' : 'Amendment'}
                      </p>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs">{log.date || log.timestamp?.split('T')[0]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Main WorkerDetailsModal Component
const WorkerDetailsModal = ({ 
  open, 
  onClose, 
  worker, 
  currencySymbol = 'AED',
  onPayment,
  onDeduction,
  onStatusChange,
  onDelete,
  onFullyPaid,
  onSettle,
  onAmendment,
  onWarrantyActivate,
  onWarrantyPeriodChange,
  onPhotoUpdate
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  
  const isLocked = worker?.status === 'runaway' || worker?.status === 'noarrival' || worker?.status === 'settled';
  
  // Reset to profile tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab('profile');
    }
  }, [open]);
  
  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('الرجاء اختيار صورة / Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة كبير جداً (الحد الأقصى 5MB) / Image too large (max 5MB)');
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        try {
          // Upload to API
          const result = await apiPut(`/api/cases/${worker.id}/photo`, {
            photoData: base64Data
          });
          
          toast.success(`✅ تم رفع الصورة بنجاح (${result.photoSizeKB}KB)`);

          // Instantly refresh every mounted photo for this worker (no reload).
          setWorkerPhotoCache(worker.id, base64Data);

          // Call parent callback to refresh data
          if (onPhotoUpdate) {
            onPhotoUpdate(worker.id, base64Data);
          }
        } catch (error) {
          toast.error('فشل رفع الصورة / Failed to upload photo');
          console.error('Photo upload error:', error);
        } finally {
          setUploadingPhoto(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('فشل قراءة الصورة / Failed to read image');
        setUploadingPhoto(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('فشل رفع الصورة / Failed to upload photo');
      setUploadingPhoto(false);
    }
    
    // Reset input
    event.target.value = '';
  };
  
  if (!worker) return null;
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 bg-transparent border-0 overflow-hidden">
        <div className="relative w-full h-full rounded-2xl overflow-hidden">
          {/* Animated Background */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${tabBackgrounds[activeTab]})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
            </motion.div>
          </AnimatePresence>
          
          {/* Content */}
          <div className="relative z-10 h-full flex flex-col max-h-[90vh]">
            {/* Fixed Header */}
            <div className="p-4 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="h-5 w-5 mr-1" />
                  Back
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(worker)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex gap-1.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl transition-all duration-300 ${
                        isActive 
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25' 
                          : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-xs font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Hidden File Input for Photo Upload */}
              <input
                type="file"
                ref={photoInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                className="hidden"
              />
              
              {/* Worker Header - Shows in ALL tabs */}
              <WorkerHeader 
                worker={worker} 
                onPhotoClick={() => photoInputRef.current?.click()}
                uploadingPhoto={uploadingPhoto}
              />
              
              {/* Tab Content */}
              <AnimatePresence mode="wait">
                {activeTab === 'profile' && (
                  <ProfileTab 
                    key="profile"
                    worker={worker}
                    onStatusChange={onStatusChange}
                    onWarrantyActivate={onWarrantyActivate}
                    onWarrantyPeriodChange={onWarrantyPeriodChange}
                    isLocked={isLocked}
                  />
                )}
                {activeTab === 'documents' && (
                  <DocumentsTab key="documents" worker={worker} />
                )}
                {activeTab === 'financial' && (
                  <FinancialTab 
                    key="financial"
                    worker={worker}
                    currencySymbol={currencySymbol}
                    onPayment={onPayment}
                    onDeduction={onDeduction}
                    onFullyPaid={onFullyPaid}
                    onSettle={onSettle}
                    onAmendment={onAmendment}
                    isLocked={isLocked}
                  />
                )}
                {activeTab === 'warranty' && (
                  <WarrantyTab 
                    key="warranty"
                    worker={worker}
                    onWarrantyActivate={onWarrantyActivate}
                    onWarrantyPeriodChange={onWarrantyPeriodChange}
                    isLocked={isLocked}
                  />
                )}
                {activeTab === 'history' && (
                  <HistoryTab key="history" worker={worker} currencySymbol={currencySymbol} />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkerDetailsModal;
